import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, X } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { useLang } from "@/context/LanguageContext";
import { artists, DISCIPLINES, DISCIPLINE_LABEL } from "@/data/artists";
import { useCatalogVersion } from "@/data/catalogLive";
import { ArtworkCard } from "@/components/latir/ArtworkCard";
import { useSEO } from "@/hooks/useSEO";

const COPY = {
  title: { es: "Artistas de LATIR", en: "LATIR Artists" },
  intro: {
    es: "Más de 50 artistas nacionales e internacionales reunidos en una exposición sobre la vida, los vínculos, la fragilidad, el cuidado y la permanencia.",
    en: "More than 50 national and international artists gathered in an exhibition about life, bonds, fragility, care and permanence.",
  },
  byName: { es: "Por nombre", en: "By name" },
  byDiscipline: { es: "Por disciplina", en: "By discipline" },
  allArtists: { es: "Todos los artistas", en: "All artists" },
  allDisciplines: { es: "Todas las disciplinas", en: "All disciplines" },
  searchArtist: { es: "Buscar artista…", en: "Search artist…" },
  noArtists: { es: "Sin coincidencias", en: "No matches" },
  countWorks: { es: "obras", en: "works" },
  empty: { es: "Sin resultados para este filtro.", en: "No results for this filter." },
  clear: { es: "Quitar filtro", en: "Clear filter" },
};

const normalize = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

export default function Artists() {
  useCatalogVersion();
  const { lang } = useLang();

  useSEO({
    title: "Artistas de LATIR | Arteconciencia El Salvador",
    description:
      "Conoce a los artistas nacionales e internacionales que participan en LATIR — El placer de vivir, una exposición presentada por Arteconciencia en El Salvador.",
    canonical: "/artistas",
  });
  const [selectedArtist, setSelectedArtist] = useState("all"); // clave = nombre normalizado
  const [selectedDisc, setSelectedDisc] = useState("all");
  const [openName, setOpenName] = useState(false);
  const [openDisc, setOpenDisc] = useState(false);

  // Lista única de nombres de artistas, ordenada alfabéticamente.
  const artistOptions = useMemo(() => {
    const map = new Map();
    for (const a of artists) {
      const key = normalize(a.name);
      if (!map.has(key)) map.set(key, { key, name: a.name });
    }
    return Array.from(map.values()).sort((a, b) =>
      a.key.localeCompare(b.key, "es")
    );
  }, []);

  // Nombre visible del filtro activo.
  const activeArtistName =
    selectedArtist === "all"
      ? null
      : artistOptions.find((o) => o.key === selectedArtist)?.name || null;
  const activeDiscLabel =
    selectedDisc === "all" ? null : DISCIPLINE_LABEL[selectedDisc][lang];

  // Filtrado: si hay artista, ignora disciplina; si hay disciplina, ignora artista.
  const filtered = useMemo(() => {
    if (selectedArtist !== "all") {
      return artists.filter((a) => normalize(a.name) === selectedArtist);
    }
    if (selectedDisc !== "all") {
      return artists.filter((a) => a.discipline === selectedDisc);
    }
    return artists;
  }, [selectedArtist, selectedDisc]);

  const handlePickArtist = (key) => {
    setSelectedArtist(key);
    if (key !== "all") setSelectedDisc("all");
    setOpenName(false);
  };
  const handlePickDisc = (key) => {
    setSelectedDisc(key);
    if (key !== "all") setSelectedArtist("all");
    setOpenDisc(false);
  };

  return (
    <div className="pt-28 md:pt-32 pb-20 md:pb-24" data-testid="page-artists">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        {/* ================= ENCABEZADO ================= */}
        <header className="mb-8 md:mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8 items-end">
            <h1
              className="lg:col-span-7 font-bodoni font-normal text-[1.85rem] sm:text-[2.4rem] lg:text-[3rem] leading-[1.05] tracking-[-0.02em]"
              style={{ color: "#9D1116" }}
              data-testid="artists-title"
            >
              {COPY.title[lang]}
            </h1>
            <p
              className="lg:col-span-5 font-editorial text-[0.98rem] sm:text-[1.05rem] leading-[1.55] text-ink/85 max-w-[52ch]"
              data-testid="artists-intro"
            >
              {COPY.intro[lang]}
            </p>
          </div>
        </header>

        {/* ================= FILTROS: DOS DROPDOWNS ================= */}
        <div
          className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-8 pb-4 mb-6 md:mb-8 border-b border-line"
          data-testid="artists-filters"
        >
          {/* Por nombre — Combobox con buscador */}
          <div className="flex flex-col gap-2 flex-1 sm:max-w-[300px]">
            <span className="font-ui uppercase tracking-[0.16em] text-[0.62rem] text-ink-mute">
              {COPY.byName[lang]}
            </span>
            <Popover open={openName} onOpenChange={setOpenName}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="filter-name-trigger"
                  className={
                    "w-full flex items-center justify-between gap-3 border-0 border-b border-line pb-2 pt-1 bg-transparent font-ui uppercase tracking-[0.16em] text-[0.74rem] transition-colors " +
                    (activeArtistName
                      ? "text-latir-red border-latir-red/60"
                      : "text-ink hover:text-ink data-[state=open]:border-latir-red")
                  }
                >
                  <span className="truncate">
                    {activeArtistName || COPY.allArtists[lang]}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 shrink-0 opacity-70"
                    strokeWidth={1.5}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[min(92vw,340px)] p-0 bg-paper-raised border-line rounded-[2px] shadow-md"
              >
                <Command className="bg-transparent">
                  <CommandInput
                    placeholder={COPY.searchArtist[lang]}
                    className="font-ui text-[0.85rem]"
                    data-testid="filter-name-search"
                  />
                  <CommandList className="max-h-[320px]">
                    <CommandEmpty className="py-6 text-center font-editorial italic text-[0.9rem] text-ink-mute">
                      {COPY.noArtists[lang]}
                    </CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__all__"
                        onSelect={() => handlePickArtist("all")}
                        className="font-ui uppercase tracking-[0.14em] text-[0.72rem] cursor-pointer"
                        data-testid="filter-name-item-all"
                      >
                        <span className="flex-1">{COPY.allArtists[lang]}</span>
                        {selectedArtist === "all" && (
                          <Check className="w-3.5 h-3.5 text-latir-red" />
                        )}
                      </CommandItem>
                      {artistOptions.map((opt) => (
                        <CommandItem
                          key={opt.key}
                          value={opt.name}
                          onSelect={() => handlePickArtist(opt.key)}
                          className="font-ui uppercase tracking-[0.14em] text-[0.72rem] cursor-pointer"
                          data-testid={`filter-name-item-${opt.key}`}
                        >
                          <span className="flex-1 truncate">{opt.name}</span>
                          {selectedArtist === opt.key && (
                            <Check className="w-3.5 h-3.5 text-latir-red" />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Por disciplina */}
          <div className="flex flex-col gap-2 flex-1 sm:max-w-[240px]">
            <span className="font-ui uppercase tracking-[0.16em] text-[0.62rem] text-ink-mute">
              {COPY.byDiscipline[lang]}
            </span>
            <Popover open={openDisc} onOpenChange={setOpenDisc}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="filter-disc-trigger"
                  className={
                    "w-full flex items-center justify-between gap-3 border-0 border-b border-line pb-2 pt-1 bg-transparent font-ui uppercase tracking-[0.16em] text-[0.74rem] transition-colors " +
                    (activeDiscLabel
                      ? "text-latir-red border-latir-red/60"
                      : "text-ink hover:text-ink data-[state=open]:border-latir-red")
                  }
                >
                  <span className="truncate">
                    {activeDiscLabel || COPY.allDisciplines[lang]}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 shrink-0 opacity-70"
                    strokeWidth={1.5}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[min(92vw,260px)] p-0 bg-paper-raised border-line rounded-[2px] shadow-md"
              >
                <ul className="py-1" role="listbox">
                  <li>
                    <button
                      type="button"
                      onClick={() => handlePickDisc("all")}
                      data-testid="filter-disc-item-all"
                      className="w-full flex items-center gap-3 px-4 py-2 text-left font-ui uppercase tracking-[0.14em] text-[0.72rem] hover:bg-paper-deep transition-colors"
                    >
                      <span className="flex-1">{COPY.allDisciplines[lang]}</span>
                      {selectedDisc === "all" && (
                        <Check className="w-3.5 h-3.5 text-latir-red" />
                      )}
                    </button>
                  </li>
                  {DISCIPLINES.map((d) => (
                    <li key={d}>
                      <button
                        type="button"
                        onClick={() => handlePickDisc(d)}
                        data-testid={`filter-disc-item-${d}`}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left font-ui uppercase tracking-[0.14em] text-[0.72rem] hover:bg-paper-deep transition-colors"
                      >
                        <span className="flex-1">{DISCIPLINE_LABEL[d][lang]}</span>
                        {selectedDisc === d && (
                          <Check className="w-3.5 h-3.5 text-latir-red" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          </div>

          {/* Contador + limpiar */}
          <div className="flex items-center gap-4 sm:ml-auto pb-1">
            {(activeArtistName || activeDiscLabel) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedArtist("all");
                  setSelectedDisc("all");
                }}
                data-testid="filters-clear"
                className="inline-flex items-center gap-1.5 font-ui uppercase tracking-[0.16em] text-[0.62rem] text-ink-mute hover:text-latir-red transition-colors"
              >
                <X className="w-3 h-3" strokeWidth={1.6} />
                {COPY.clear[lang]}
              </button>
            )}
            <span
              className="font-ui text-[0.72rem] tracking-[0.14em] text-ink-mute tabular-nums whitespace-nowrap"
              data-testid="artists-count"
            >
              {String(filtered.length).padStart(2, "0")} {COPY.countWorks[lang]}
            </span>
          </div>
        </div>

        {/* ================= GRID DE OBRAS ================= */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${selectedArtist}-${selectedDisc}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="artists-grid"
          >
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-x-4 md:gap-x-6 gap-y-8 md:gap-y-12">
                {filtered.map((a, i) => (
                  <ArtworkCard
                    key={a.slug}
                    artist={a}
                    index={i + 1}
                    priority={i < 3}
                  />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <p className="font-editorial italic text-[1.25rem] text-ink-soft">
                  {COPY.empty[lang]}
                </p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
